import {
  createCameraWebRtcLease, deleteCameraWebRtcLease, getCameraHlsUrl,
  getCameraIceServers, getCameraPlaybackOptions, renewCameraWebRtcLease,
  type CameraStreamChoice,
} from './cameraMedia';

/** A single owner-bound browser media session, reused by all canvas surfaces for a camera. */
export class NativeCameraTransport {
  readonly video = document.createElement('video');
  private readonly abort = new AbortController();
  private peer: RTCPeerConnection | null = null;
  private leaseId: string | null = null;
  private renewal: number | null = null;
  private disconnected: number | null = null;
  private firstFrameTimeout: number | null = null;
  private progressCheck: number | null = null;
  private progressVisibility: (() => void) | null = null;
  private hlsAvailable = false;
  private hlsStarted = false;
  private stopped = false;

  constructor(private readonly cameraId: string, private readonly stream: CameraStreamChoice,
    private readonly onFailure: () => void) {
    this.video.autoplay = true;
    this.video.muted = true;
    this.video.playsInline = true;
    this.video.crossOrigin = 'use-credentials';
    this.video.onloadeddata = () => {
      this.clearFirstFrameTimeout();
      this.watchProgress();
    };
  }

  async start(): Promise<'webrtc' | 'hls' | null> {
    let options;
    try { options = await getCameraPlaybackOptions(this.cameraId, this.abort.signal, this.stream); }
    catch { return null; }
    if (this.stopped || options.preferMjpeg) return null;
    this.hlsAvailable = options.hls && Boolean(this.video.canPlayType('application/vnd.apple.mpegurl'));
    if (options.webRtc && typeof RTCPeerConnection !== 'undefined') {
      try {
        const iceServers = await getCameraIceServers(this.cameraId, this.stream, this.abort.signal);
        if (this.stopped) return null;
        const peer = new RTCPeerConnection({ iceServers });
        this.peer = peer;
        peer.addTransceiver('video', { direction: 'recvonly' });
        peer.ontrack = (event) => {
          if (!this.stopped && this.peer === peer) {
            if (this.stream === 'main' && 'jitterBufferTarget' in event.receiver) {
              try {
                // Relayed links can deliver native frames in bursts separated by about one second.
                event.receiver.jitterBufferTarget = 1_000;
              } catch {
                // Unsupported setters retain the browser's automatic buffering.
              }
            }
            this.video.srcObject = event.streams[0] ?? new MediaStream([event.track]);
            void this.video.play().catch(() => { if (this.peer === peer) this.fail(); });
          }
        };
        peer.onconnectionstatechange = () => {
          if (this.stopped || this.peer !== peer) return;
          if (peer.connectionState === 'failed' || peer.connectionState === 'closed') this.fail();
          if (peer.connectionState === 'disconnected' && this.disconnected === null) {
            this.disconnected = window.setTimeout(() => {
              this.disconnected = null;
              if (peer.connectionState === 'disconnected') this.fail();
            }, 5_000);
          }
          if (peer.connectionState === 'connected' && this.disconnected !== null) {
            window.clearTimeout(this.disconnected);
            this.disconnected = null;
          }
        };
        await peer.setLocalDescription(await peer.createOffer());
        await this.waitForIce(peer);
        if (this.stopped) return null;
        const lease = await createCameraWebRtcLease(this.cameraId, this.stream,
          peer.localDescription?.sdp ?? '', this.abort.signal);
        this.leaseId = lease.id;
        if (this.stopped) { this.closeWebRtc(); return null; }
        await peer.setRemoteDescription({ type: 'answer', sdp: lease.answer });
        if (this.stopped) return null;
        if (this.hlsStarted) return 'hls';
        if (this.video.readyState < 2)
          this.firstFrameTimeout = window.setTimeout(() => {
            this.firstFrameTimeout = null;
            if (this.video.readyState < 2) this.fail();
            else this.watchProgress();
          }, this.stream === 'main' ? 60_000 : 8_000);
        else this.watchProgress();
        this.renewal = window.setInterval(() => {
          if (this.leaseId) void renewCameraWebRtcLease(this.cameraId, this.leaseId)
            .catch(() => this.fail(false));
        }, Math.max(15, lease.leaseSeconds / 2) * 1_000);
        return 'webrtc';
      } catch {
        if (!this.hlsStarted) this.closeWebRtc();
      }
    }
    if (this.stopped) return null;
    return this.hlsStarted || this.startHls() ? 'hls' : null;
  }

  stop() {
    if (this.stopped) return;
    this.stopped = true;
    this.abort.abort();
    this.video.onerror = null;
    this.video.onloadeddata = null;
    this.video.pause();
    this.video.removeAttribute('src');
    this.video.srcObject = null;
    this.video.load();
    this.closeWebRtc();
  }

  private watchProgress() {
    if (this.progressCheck !== null || this.stopped) return;
    const position = () => this.video.getVideoPlaybackQuality?.().totalVideoFrames ?? this.video.currentTime;
    let time = position();
    let advancedAt = performance.now();
    this.progressVisibility = () => { time = position(); advancedAt = performance.now(); };
    document.addEventListener('visibilitychange', this.progressVisibility);
    this.progressCheck = window.setInterval(() => {
      const now = performance.now();
      if (document.visibilityState !== 'visible' || position() !== time) {
        time = position();
        advancedAt = now;
      } else if (now - advancedAt >= 15_000) this.fail(!this.hlsStarted);
    }, 2_000);
  }

  private startHls() {
    if (this.stopped || !this.hlsAvailable || this.hlsStarted) return false;
    this.hlsStarted = true;
    this.closeWebRtc();
    this.video.srcObject = null;
    this.video.onerror = () => this.fail(false);
    this.video.src = getCameraHlsUrl(this.cameraId, this.stream);
    this.firstFrameTimeout = window.setTimeout(() => {
      this.firstFrameTimeout = null;
      if (this.video.readyState < 2) this.fail(false);
      else this.watchProgress();
    }, this.stream === 'main' ? 60_000 : 8_000);
    void this.video.play().catch(() => this.fail(false));
    return true;
  }

  private fail(allowHls = true) {
    if (this.stopped) return;
    if (allowHls && this.startHls()) return;
    this.stop();
    this.onFailure();
  }

  private clearFirstFrameTimeout() {
    if (this.firstFrameTimeout !== null) window.clearTimeout(this.firstFrameTimeout);
    this.firstFrameTimeout = null;
  }

  private closeWebRtc() {
    this.clearFirstFrameTimeout();
    if (this.progressCheck !== null) window.clearInterval(this.progressCheck);
    this.progressCheck = null;
    if (this.progressVisibility) document.removeEventListener('visibilitychange', this.progressVisibility);
    this.progressVisibility = null;
    if (this.renewal !== null) window.clearInterval(this.renewal);
    if (this.disconnected !== null) window.clearTimeout(this.disconnected);
    this.renewal = null;
    this.disconnected = null;
    if (this.peer) {
      this.peer.ontrack = null;
      this.peer.onconnectionstatechange = null;
      this.peer.close();
    }
    this.peer = null;
    if (this.leaseId) {
      const id = this.leaseId;
      this.leaseId = null;
      void deleteCameraWebRtcLease(this.cameraId, id).catch(() => {
        // The server expires abandoned leases and closes the upstream session.
      });
    }
  }

  private async waitForIce(peer: RTCPeerConnection) {
    if (peer.iceGatheringState === 'complete') return;
    await new Promise<void>((resolve) => {
      const done = () => {
        peer.removeEventListener('icegatheringstatechange', changed);
        this.abort.signal.removeEventListener('abort', done);
        window.clearTimeout(timeout);
        resolve();
      };
      const changed = () => { if (peer.iceGatheringState === 'complete') done(); };
      const timeout = window.setTimeout(done, 8_000);
      peer.addEventListener('icegatheringstatechange', changed);
      this.abort.signal.addEventListener('abort', done, { once: true });
    });
  }
}
